import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { schedulerApi, type SchedulerRunsParams } from '@/api/scheduler';
import { schedulerKeys, accountKeys } from '@/lib/queryKeys';
import { GC_TIMES, POLL_INTERVALS, STALE_TIMES } from '@/lib/constants';

export function useSchedulerStatus() {
  const { user } = useAuth();
  return useQuery({
    enabled: user?.role === 'ADMIN',
    queryKey: schedulerKeys.status(),
    queryFn: schedulerApi.status,
    gcTime: GC_TIMES.default,
    // staleTime et refetchInterval sont symétriques : quand un run tourne, cadence rapide
    // (5 s) pour voir la fin du cycle vite ; sinon cadence normale (30 s). Garder les
    // deux alignés évite qu'un remount de composant (OpsScreen, OverviewScreen, RightRail)
    // déclenche un refetch parasite entre deux ticks.
    staleTime: (query) => {
      const running = query.state.data?.currentlyRunning ?? 0;
      return running > 0 ? POLL_INTERVALS.schedulerStatusActive : POLL_INTERVALS.schedulerStatus;
    },
    refetchInterval: (query) => {
      const running = query.state.data?.currentlyRunning ?? 0;
      return running > 0 ? POLL_INTERVALS.schedulerStatusActive : POLL_INTERVALS.schedulerStatus;
    },
    refetchIntervalInBackground: false,
  });
}

export function useSchedulerRuns(params: SchedulerRunsParams = {}) {
  const { user } = useAuth();
  return useQuery({
    enabled: user?.role === 'ADMIN',
    queryKey: schedulerKeys.runs(params),
    queryFn: () => schedulerApi.runs(params),
    staleTime: STALE_TIMES.medium,
    gcTime: GC_TIMES.default,
    placeholderData: keepPreviousData,
  });
}

export function useAccountsHealth() {
  const { user } = useAuth();
  return useQuery({
    enabled: user?.role === 'ADMIN',
    queryKey: accountKeys.health(),
    queryFn: schedulerApi.accountsHealth,
    staleTime: STALE_TIMES.short,
    gcTime: GC_TIMES.default,
    refetchInterval: POLL_INTERVALS.accountsHealth,
    refetchIntervalInBackground: false,
  });
}
